import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { Button, Grid, Stack, Tooltip, useTheme } from "@mui/material"
import EditIcon from "@mui/icons-material/Edit"
import InfoOutlined from "@mui/icons-material/InfoOutlined"

import useLimitedEvents from "helpers/hooks/useLimitedEvents"
import usePermissions from "helpers/hooks/usePermissions"
import useSelectOptions from "helpers/hooks/useSelectOptions"
import { DATE_RANGE_TIME } from "helpers/utils/constants"
import type { AlertSettingAccordion } from "types/alerts.types"
import AlertsContext from "widgets/alerts/AlertsContext"
import EventsTable from "widgets/alerts/EventsTable"
import AlertDisabledNotice from "widgets/alerts/AlertDisabledNotice"
import TabButtonGroup from "widgets/common/TabButtonGroup"
import { BodyContainer } from "widgets/styled/containers"
import AccordionSelect from "widgets/dates/AccordionSelect"
import { setAccordionDateRange } from "widgets/dates/helpers"
import { AlertSettingDrawerContext } from "widgets/alerts/settings/AlertSettingDrawerContext"
import { AlertChart } from "./AlertChart"
import AlertConfiguration from "./AlertConfiguration"

interface AlertAccordionDetailsContentProps {
  alert: AlertSettingAccordion
  setActive: (active: boolean) => void
  isSettingActive: boolean
}

const EVENTS_TAB = 0
const CHART_TAB = 1
const CONFIGURATION_TAB = 2
const TAB_BUTTONS = [
  { name: "alerts.CHART", value: CHART_TAB },
  { name: "generic.EVENTS", value: EVENTS_TAB },
  { name: "alerts.CONFIGURATION", value: CONFIGURATION_TAB },
]

const AlertAccordionDetailsContent = ({
  alert,
  setActive,
  isSettingActive,
}: AlertAccordionDetailsContentProps) => {
  const theme = useTheme()
  const { t } = useTranslation()
  // deadman alerts have no chart
  const hasChart = useMemo(() => alert.type !== "deadman", [alert.type])
  const tabButtons = useMemo(
    () =>
      hasChart ? TAB_BUTTONS : TAB_BUTTONS.filter(({ value }) => value !== CHART_TAB),
    [hasChart],
  )
  const [tab, setTab] = useState(hasChart ? CHART_TAB : EVENTS_TAB)
  const activeChart = hasChart && tab === CHART_TAB
  const activeEvents = tab === EVENTS_TAB
  const activeConfiguration = tab === CONFIGURATION_TAB
  // Pages without the drawer, and group settings, stay read only.
  const drawer = useContext(AlertSettingDrawerContext)
  const { isEditor } = usePermissions()
  const canEdit = Boolean(drawer) && isEditor && alert.entity !== "group"
  const isDisabled = alert.state === "disabled"
  const { multipleDateRange, dispatchMultipleDateRange } = useContext(AlertsContext)
  const { events, isLoading } = useLimitedEvents({ settingId: alert.id })
  const { getDateReferencesByAlertState, getSelectOptionsByAlertState } =
    useSelectOptions()
  const dateReferences = useMemo(
    () => getDateReferencesByAlertState(alert.state),
    [alert.state, getDateReferencesByAlertState],
  )
  const isTriggered = alert.state === "open" || alert.state === "silenced"
  const alertInfo = useMemo(() => {
    return { id: alert.id, triggeredAt: alert.triggered_at }
  }, [alert.id, alert.triggered_at])

  // update only when this accordion date range changes
  const alertDateRange = multipleDateRange[alert.id]
  const dateRange = useMemo(() => alertDateRange, [alertDateRange])

  const interval = useRef<ReturnType<typeof setInterval> | undefined>(undefined)
  const updateDateReference = useCallback(
    (dateReferenceName: string) => {
      if (interval.current) {
        clearInterval(interval.current)
      }
      interval.current = undefined
      setAccordionDateRange(
        alertInfo,
        dateReferenceName,
        dateReferences,
        dispatchMultipleDateRange,
      )
      interval.current = setInterval(() => {
        setAccordionDateRange(
          alertInfo,
          dateReferenceName,
          dateReferences,
          dispatchMultipleDateRange,
        )
      }, DATE_RANGE_TIME)
    },
    [alertInfo, dateReferences, dispatchMultipleDateRange],
  )

  useEffect(() => {
    // Set initial context value
    const dateReferenceName = isTriggered ? "triggerWindow" : "lastWeek"
    updateDateReference(dateReferenceName)
    // This is only updated after the first render.
    // Intentionally not re-evaluating on update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!dateRange) return
    const options = getSelectOptionsByAlertState(alert.state)
    const isValidOption = options.some((opt) => opt.value === dateRange.range)
    if (!isValidOption) {
      updateDateReference("lastWeek")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alert.state])

  useEffect(() => {
    return () => {
      if (interval.current) {
        clearInterval(interval.current)
      }
    }
  }, [interval])

  return (
    <>
      {dateRange && (
        <BodyContainer
          disableGutters
          maxWidth="xl"
          // Below md the content lines up with the header text, so the table gets the width.
          sx={{ padding: "1rem", [theme.breakpoints.down("md")]: { px: 0.5 } }}
        >
          {isDisabled && (
            <AlertDisabledNotice
              canEnable={isEditor}
              isLoading={isSettingActive}
              onEnable={() => setActive(true)}
            />
          )}
          <Grid container spacing={2} justifyContent={"space-between"}>
            <Grid item xs={12} sm="auto">
              <TabButtonGroup buttons={tabButtons} tab={tab} setTab={setTab} />
            </Grid>
            <Grid item xs={12} sm>
              {activeChart && (
                <Stack
                  direction={"row"}
                  alignItems={"center"}
                  sx={{ [theme.breakpoints.up("sm")]: { justifyContent: "end" } }}
                >
                  <AccordionSelect
                    setSelectedDateReference={updateDateReference}
                    dateReferenceName={dateRange.range}
                    alertState={alert.state}
                  />
                  {isTriggered && (
                    <Tooltip title={t("alerts.TRIGGER_WINDOW_INFO")}>
                      <Stack m={1}>
                        <InfoOutlined color="primary" />
                      </Stack>
                    </Tooltip>
                  )}
                </Stack>
              )}
              {activeConfiguration && canEdit && (
                <Stack
                  direction={"row"}
                  sx={{ [theme.breakpoints.up("sm")]: { justifyContent: "end" } }}
                >
                  <Button
                    variant="text"
                    size="small"
                    startIcon={<EditIcon fontSize="small" />}
                    onClick={() => drawer?.openEdit(alert)}
                  >
                    {t("generic.EDIT")}
                  </Button>
                </Stack>
              )}
            </Grid>
          </Grid>
          <Stack mt={"1.5rem"}>
            {activeEvents && <EventsTable events={events} isLoading={isLoading} />}
            {activeChart && <AlertChart alert={alert} />}
            {activeConfiguration && <AlertConfiguration alert={alert} />}
          </Stack>
        </BodyContainer>
      )}
    </>
  )
}

export default AlertAccordionDetailsContent
